import {
  InvoiceApplicationFormData,
  InvoiceApplicationFormDataInput,
  SelectionStateEnum,
} from '@monetize/types/app';
import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';

interface UseTransactionApplicationSelectionParams {
  formState: UseFormReturn<
    InvoiceApplicationFormDataInput,
    unknown,
    InvoiceApplicationFormData
  >;
  /**
   * Full amount of Payment or Credit
   */
  totalAvailableAmount: number;
}

/**
 * Table-level "select all" logic shared by every transaction application table
 * (apply, refund, …). Keeps the header checkbox in sync with the row selection
 * and distributes / clears applied amounts when it is toggled.
 */
export const useTransactionApplicationSelection = ({
  formState,
  totalAvailableAmount,
}: UseTransactionApplicationSelectionParams) => {
  const { watch, setValue } = formState;
  const checkedState = watch('checkedState');
  const rows = watch('rows');

  const allSelected =
    rows.length > 0 &&
    rows.every((row) => row.checkedState === SelectionStateEnum.ALL);

  const someSelected =
    !allSelected &&
    rows.some(
      (row) =>
        row.checkedState === SelectionStateEnum.ALL ||
        row.checkedState === SelectionStateEnum.SOME,
    );

  useEffect(() => {
    if (allSelected) {
      setValue('checkedState', SelectionStateEnum.ALL);
    } else if (someSelected) {
      setValue('checkedState', SelectionStateEnum.SOME);
    } else {
      setValue('checkedState', SelectionStateEnum.NONE);
    }
  }, [allSelected, setValue, someSelected]);

  const handleToggleSelectAll = () => {
    if (
      checkedState === SelectionStateEnum.ALL ||
      checkedState === SelectionStateEnum.SOME
    ) {
      setValue('checkedState', SelectionStateEnum.NONE);
      setValue(
        'rows',
        rows.map((row) => ({
          ...row,
          appliedAmount: 0,
          checkedState: SelectionStateEnum.NONE,
        })),
      );
    } else {
      let amountAvailable = totalAvailableAmount;
      setValue(
        'rows',
        rows.map((row) => {
          const { amountDueWithoutApplied } = row;
          const appliedAmount = Math.min(
            amountAvailable,
            amountDueWithoutApplied,
          );
          amountAvailable -= appliedAmount;
          return { ...row, appliedAmount };
        }),
      );
    }
  };

  return { checkedState, rows, handleToggleSelectAll };
};
